import { useEffect, useState } from 'react';
import { supabase } from '@/shared/lib/supabase';

/**
 * Pending transfer-in orders for the current customer — read-side only.
 * Used by `DomainsListPage` to render a "Pending — in progress" strip
 * above the active-domains table during the gap between paying for a
 * transfer and the `domain_link` row being materialised by the saga.
 *
 * Filters on the customer's own RLS scope (no need for a customer_id
 * predicate — RLS policy `domain_order_select_own` enforces it).
 *
 * Capped at 10 most recent (BRD §10 R5 mitigation against runaway
 * inventory).
 */

export interface PendingTransferOrder {
  id: string;
  domain_name: string;
  status: string;
  invoice_id: string | null;
  invoice_lifecycle_state: string | null;
  invoice_total: number | null;
  created_at: string;
  updated_at: string;
}

interface State {
  data: PendingTransferOrder[];
  loading: boolean;
  error: string | null;
}

export function usePendingTransferOrders() {
  const [state, setState] = useState<State>({ data: [], loading: true, error: null });

  useEffect(() => {
    let cancelled = false;

    async function load() {
      const { data, error } = await supabase
        .from('domain_registration_order')
        .select(
          'id, domain_name, status, invoice_id, created_at, updated_at, invoice:invoice_id(lifecycle_state, total)',
        )
        .eq('step', 'transfer')
        .not('status', 'in', '(complete,cancelled,failed)')
        .order('created_at', { ascending: false })
        .limit(10);

      if (cancelled) return;
      if (error) {
        setState({ data: [], loading: false, error: error.message });
        return;
      }

      const rows = (data ?? []).map((row) => {
        const r = row as Record<string, unknown> & { invoice?: { lifecycle_state?: string; total?: number } | null };
        return {
          id: r.id as string,
          domain_name: r.domain_name as string,
          status: r.status as string,
          invoice_id: (r.invoice_id as string | null) ?? null,
          invoice_lifecycle_state: r.invoice?.lifecycle_state ?? null,
          invoice_total: r.invoice?.total ?? null,
          created_at: r.created_at as string,
          updated_at: r.updated_at as string,
        };
      });

      setState({ data: rows, loading: false, error: null });
    }

    void load();
    return () => {
      cancelled = true;
    };
  }, []);

  return state;
}
